// Palworld merchandise keyword cluster (2026).
//
// Topic selection is forecast from the equivalent Pokemon merchandise search
// patterns (plush, apparel, accessories, TCG accessories, gifting, collector
// figures) mapped onto Palworld demand. Volumes checked in Semrush before
// authoring: "palworld plushies" 390/mo KD 17, "palworld plush" 320/mo KD 27,
// "palworld figures" 70/mo KD 15, plus a long tail of sub-50/mo terms at KD 0.
//
// Structure mirrors src/lib/guides-products-2026.ts: each topic expands into
// exactly five AEO-shaped articles (overview, step-by-step, reference,
// mistakes, FAQ). Nothing about price, stock or print run is invented — the
// guidance is about verifying official product lines and avoiding counterfeit
// or print-on-demand listings, which is checkable by the reader.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-plushies-buying-guide",
    title: "Palworld Plushies Buying Guide",
    desc: "How to tell an officially licensed Palworld plush from a print-on-demand copy, which Pals have confirmed plush lines, and what to check on a listing before you pay.",
    keywords:
      "palworld plushies, palworld plush, lamball plush, cattiva plush, depresso plush, official palworld plush, palworld stuffed animal, where to buy palworld plush",
    hub: "/pals",
    hubLabel: "Palworld Pal database",
    tag: "Merch",
    question: "Where do you buy official Palworld plushies?",
    answer:
      "Official Palworld plushies come from licensed partners announced by Pocketpair — currently The Loyal Subjects for plush and blind-box lines, alongside the Palworld storefronts linked from the official site. A licensed plush always names its licensee on the hang tag and the sewn-in tag; a listing that shows only a render, has no licensee name and ships in 3–4 weeks is almost always an unlicensed print-on-demand or drop-ship copy.",
    steps: [
      [
        "Start from the official site's store link, not a search result",
        "Search results for plush terms are dominated by marketplaces and print-on-demand shops. Open the Palworld official site first and follow its own store or partner link so you know the seller is licensed.",
      ],
      [
        "Check the licensee name appears on the product photos",
        "Licensed plush carry a printed hang tag with the licensee and Pocketpair copyright. Ask the seller for a photo of the tag if the listing does not show one — a refusal is your answer.",
      ],
      [
        "Match the Pal to a confirmed plush line",
        "Only some Pals have announced plush. If the Pal you want has no announced line, any plush of it is fan-made. Look the Pal up in the Pal database first so you know which one you are actually buying.",
      ],
      [
        "Compare size and material against the listed spec",
        "Official listings state height in centimetres and the fill material. A copy usually omits both or lists a size that does not match the photos.",
      ],
      [
        "Buy in the release window, not the resale window",
        "Plush lines sell through quickly and then reappear at marked-up resale. Pre-order or buy on release from the licensed retailer rather than paying a resale premium for the same item.",
      ],
    ],
    data: [
      ["Announced plush licensee", "The Loyal Subjects, which handles Palworld plush and blind-box product lines."],
      ["Tag check", "Licensed plush show the licensee name plus a Pocketpair copyright line on the hang tag and sewn-in tag."],
      ["Typical unlicensed tell", "Render-only photos, no licensee name, long overseas shipping estimate, and the same image reused across several shops."],
      ["Pal coverage", "Plush lines cover a subset of the roster; the full official Pal list is in the Pal database at /pals."],
      ["Where to verify a Pal", "Open the Pal's page on Pal Stars to confirm it is an official Pal before buying merchandise of it."],
    ],
    pitfalls: [
      [
        "Buying a plush of a Pal with no announced line",
        "If no licensee has announced that Pal, the plush is fan-made. Check the Pal database and the official announcement before ordering.",
      ],
      [
        "Treating a marketplace 'official' badge as a licence",
        "Marketplace badges describe the seller account, not the licence. Only the licensee name on the product tag confirms licensing.",
      ],
      [
        "Paying resale price during a restock",
        "Resale listings often sit alongside a live licensed listing. Check the licensed retailer for stock before accepting a marked-up price.",
      ],
      [
        "Assuming size from the photo",
        "Plush photos are usually shot without scale. Read the stated centimetre height; a 'large' plush in a copy listing is frequently palm-sized.",
      ],
    ],
    faq: [
      [
        "Are there official Lamball plushies?",
        "Lamball is one of the most requested plush subjects and appears in licensed Palworld plush marketing. Confirm the specific product on the licensee's own store page before buying, because Lamball is also the single most copied Pal in unlicensed plush listings.",
      ],
      [
        "How much do official Palworld plushies cost?",
        "Prices are set by the licensee and retailer and change by size and region, so treat any figure quoted second-hand as unreliable. Check the licensed store page for the current price rather than a resale listing.",
      ],
      [
        "How can I spot a fake Palworld plush?",
        "Look for a missing licensee hang tag, render-only photos, no stated size or material, an unusually long shipping estimate, and the same product image appearing on several unrelated shops. Any two of those together mean it is not licensed.",
      ],
      [
        "Do Palworld plushies come in blind boxes?",
        "Yes — The Loyal Subjects' Palworld line includes blind-box products, which means the specific Pal is randomised. Read the mystery box and blind box guide before buying a sealed case.",
      ],
      [
        "Is a fan-made plush illegal to buy?",
        "Buying is not your legal risk, but unlicensed plush are not made to the licensee's safety or material spec and cannot be returned as a licensed product. Pal Stars only links to official Palworld destinations.",
      ],
    ],
  },
  {
    slug: "palworld-apparel-shirts-and-hoodies",
    title: "Palworld Apparel: Shirts and Hoodies",
    desc: "Official Palworld shirts, hoodies and apparel: how licensed apparel is labelled, how to size it, and how to avoid the print-on-demand listings that dominate apparel search results.",
    keywords:
      "palworld shirt, palworld hoodie, palworld t shirt, palworld apparel, palworld clothing, official palworld shirt, palworld merch clothing",
    hub: "/guides",
    hubLabel: "Palworld guide library",
    tag: "Merch",
    question: "Is there official Palworld apparel?",
    answer:
      "Yes. Official Palworld apparel is sold through the storefronts and licensed partners linked from the Palworld official site, and every licensed garment carries a printed neck label or care label with the licensee and a Pocketpair copyright line. Almost every other Palworld shirt or hoodie you find in search is unlicensed print-on-demand, which is why apparel keywords show a competition score close to 1 while search volume stays small.",
    steps: [
      [
        "Open the official site's store link first",
        "Apparel search results are the most print-on-demand-heavy category in Palworld merchandise. Reaching the store through the official site removes the guesswork entirely.",
      ],
      [
        "Read the neck and care label in the photos",
        "Licensed apparel prints the licensee and copyright on the label. Print-on-demand garments carry only the blank manufacturer's label (Gildan, Bella+Canvas and similar).",
      ],
      [
        "Use the seller's own size chart, not your usual size",
        "Licensed Palworld apparel is frequently produced to Japanese sizing, which runs roughly one size smaller than US sizing. Measure a shirt you own and compare against the chart in centimetres.",
      ],
      [
        "Check the print method against the price",
        "Screen-printed and embroidered licensed garments state the method. A cheap direct-to-garment print with a visible rectangular film edge is the usual print-on-demand signature.",
      ],
      [
        "Keep the order confirmation for returns",
        "Licensed retailers accept returns on unworn apparel. Drop-ship listings routinely decline them, so the difference matters most after the parcel arrives.",
      ],
    ],
    data: [
      ["Licence marker", "Neck or care label naming the licensee plus a Pocketpair copyright line."],
      ["Unlicensed marker", "Blank blank-goods label only, with the design printed by direct-to-garment."],
      ["Sizing note", "Japanese-market sizing runs roughly one size smaller than US sizing; always read the centimetre chart."],
      ["Search behaviour", "Apparel keywords carry very high advertiser competition against low volume, because print-on-demand shops bid on them."],
      ["Return policy", "Licensed retailers accept unworn returns; drop-ship sellers usually do not."],
    ],
    pitfalls: [
      [
        "Ordering your usual US size",
        "Japanese-sized official apparel runs small. Order from the centimetre chest measurement, not the letter size.",
      ],
      [
        "Assuming a big storefront means a licence",
        "Large marketplaces host print-on-demand shops. The label in the product photos, not the storefront, tells you whether it is licensed.",
      ],
      [
        "Paying for 'exclusive' art",
        "Unlicensed shops advertise designs no licensee ever produced. Novel artwork on a Palworld garment is a warning sign, not a feature.",
      ],
      [
        "Ignoring the shipping estimate",
        "A three-to-four-week estimate on a garment almost always means it is printed to order overseas after you pay.",
      ],
    ],
    faq: [
      [
        "Where can I buy an official Palworld shirt?",
        "Through the storefront linked from the Palworld official site or a named licensed partner. Search results for the term are dominated by print-on-demand shops, so start from the official site rather than a search listing.",
      ],
      [
        "Do official Palworld hoodies run small?",
        "Often yes. Licensed apparel produced for the Japanese market is sized smaller than the same letter size in the US. Compare the centimetre chest measurement against a hoodie you already own.",
      ],
      [
        "How do I tell a print-on-demand Palworld shirt from a licensed one?",
        "Check the neck label. Licensed garments name the licensee and Pocketpair; print-on-demand garments show only a blank-goods brand such as Gildan or Bella+Canvas.",
      ],
      [
        "Is Palworld apparel restocked?",
        "Licensed apparel runs are produced in batches and restock irregularly. Sign up to the licensed retailer's stock notification rather than buying a resale listing during a gap.",
      ],
      [
        "Can I return official Palworld apparel?",
        "Licensed retailers generally accept unworn returns within their stated window. Confirm the window before ordering, because drop-ship listings usually exclude returns entirely.",
      ],
    ],
  },
  {
    slug: "palworld-accessories-keychains-stickers-posters",
    title: "Palworld Keychains, Stickers & Posters",
    desc: "The small-item Palworld merchandise category — keychains, acrylic stands, stickers, posters and bags — with the checks that separate licensed goods from bootleg reprints.",
    keywords:
      "palworld keychain, palworld stickers, palworld poster, palworld backpack, palworld acrylic stand, palworld pin, palworld accessories, palworld small merch",
    hub: "/guides",
    hubLabel: "Palworld guide library",
    tag: "Merch",
    question: "What Palworld accessories are officially available?",
    answer:
      "Palworld's licensed accessory category covers keychains, acrylic stands, pins, stickers, posters and bags, sold through the official storefront and licensed partners. Accessories are the easiest category to counterfeit because unit cost is low, so the practical check is the printed copyright line on the backing card or packaging — licensed accessories always carry one, bootleg reprints almost never do.",
    steps: [
      [
        "Decide whether you want packaged or loose",
        "Licensed accessories ship on a printed backing card or in printed packaging. A loose keychain in a poly bag has no way to prove its licence.",
      ],
      [
        "Zoom into the backing card copyright line",
        "Every licensed accessory prints a Pocketpair copyright and the licensee. Ask for a close-up photo if the listing photo is too low-resolution to read it.",
      ],
      [
        "Check the art against official artwork",
        "Bootleg accessories frequently use in-game screenshots or upscaled art. Compare against the Pal's official artwork on the Pal database page before buying.",
      ],
      [
        "Buy sticker and poster sets from one source",
        "Mixed-source sets are how bootleg sheets enter an otherwise legitimate order. A single licensed set keeps the whole order verifiable.",
      ],
      [
        "Store posters rolled, not folded",
        "Licensed posters ship rolled in a tube. A poster that arrives folded in a flat mailer was almost certainly reprinted by the seller.",
      ],
    ],
    data: [
      ["Categories", "Keychains, acrylic stands, pins, stickers, posters and bags."],
      ["Licence marker", "Printed Pocketpair copyright plus licensee name on the backing card or packaging."],
      ["Highest counterfeit risk", "Loose keychains and sticker sheets, because unit cost is low and packaging is easy to omit."],
      ["Artwork check", "Compare against the Pal's official artwork on its /pals page; bootlegs often use upscaled screenshots."],
      ["Shipping tell", "Licensed posters ship rolled in a tube; folded delivery indicates a seller reprint."],
    ],
    pitfalls: [
      [
        "Buying loose accessories with no packaging",
        "Without a backing card there is no printed licence to verify. Insist on packaged goods for anything you intend to collect.",
      ],
      [
        "Ignoring low-resolution artwork",
        "Blurry or over-sharpened art on a keychain means an upscaled source image, which no licensee would ship.",
      ],
      [
        "Assuming bundle discounts mean overstock",
        "Deep bundle discounts on accessories usually indicate a bulk bootleg run, not clearance of licensed stock.",
      ],
      [
        "Mixing sources in one order",
        "One bootleg sheet in an otherwise licensed order still ends up in your collection. Keep each order to a single verified seller.",
      ],
    ],
    faq: [
      [
        "Are there official Palworld keychains?",
        "Yes, keychains and acrylic stands are part of the licensed accessory category. Verify by reading the Pocketpair copyright and licensee name printed on the backing card in the listing photos.",
      ],
      [
        "Where can I buy official Palworld stickers?",
        "From the storefront linked on the Palworld official site or a named licensed partner. Sticker sheets are the single most reprinted Palworld item, so avoid loose sheets sold without printed packaging.",
      ],
      [
        "Are Palworld posters officially printed?",
        "Licensed posters exist and ship rolled in a tube with a printed copyright line. A folded poster in a flat mailer is a seller reprint, not a licensed print.",
      ],
      [
        "Is there an official Palworld backpack?",
        "Bags appear in the licensed accessory category, but availability changes by region and season. Check the official storefront rather than a marketplace listing, which is usually unlicensed for this item.",
      ],
      [
        "Do accessories come with authenticity cards?",
        "Not usually. The printed copyright line on the packaging is the licence marker for accessories; only higher-value collectables ship with a separate certificate.",
      ],
    ],
  },
  {
    slug: "palworld-tcg-accessories-sleeves-playmats-boxes",
    title: "Palworld TCG Sleeves & Playmats",
    desc: "Card-game accessories for the Palworld OFFICIAL CARD GAME — sleeves, playmats, deck boxes and sealed booster boxes — with sizing, legality and sealed-product checks.",
    keywords:
      "palworld card sleeves, palworld playmat, palworld tcg booster box, palworld deck box, palworld tcg accessories, palworld sleeve and card set, palworld booster box english",
    hub: "/deck-builder",
    hubLabel: "Palworld deck builder",
    tag: "TCG",
    question: "What accessories do you need for the Palworld card game?",
    answer:
      "For sanctioned Palworld OFFICIAL CARD GAME play you need opaque-backed sleeves that are uniform across the whole deck, a deck box, and optionally a playmat. Bushiroad has released a Palworld Sleeve & Card Set, and English booster boxes carry the same E-prefixed set codes as the cards inside them. Uniform, undamaged sleeves are the only accessory that affects deck legality — everything else is comfort.",
    steps: [
      [
        "Size the sleeves to the card, not the brand name",
        "Palworld OFFICIAL CARD GAME cards are standard size, so standard-size sleeves fit. Buy one pack more than the deck needs so you can replace a scuffed sleeve mid-event without breaking uniformity.",
      ],
      [
        "Keep the whole deck in identical sleeves",
        "Mixed sleeve backs make the deck's cards distinguishable from behind, which is a marked-card problem at any sanctioned event. One brand, one color, one batch.",
      ],
      [
        "Verify a booster box by its printed set code",
        "An English box lists the E-prefixed set code, such as EBP01 for Dawn of Palpagos. A box advertised as BP01 without the English prefix is a different regional printing.",
      ],
      [
        "Check the seal before opening anything you may resell",
        "Resealed boxes are the main sealed-product risk. Inspect the factory seal edges and the case weight before opening if resale value matters to you.",
      ],
      [
        "Build the list before you buy the singles",
        "Sleeve count follows deck size. Finalise the 50-card list in the deck builder first, then buy sleeves and singles once, not twice.",
      ],
    ],
    data: [
      ["Card size", "Standard TCG size, so standard-size sleeves and standard deck boxes fit."],
      ["Official accessory product", "Bushiroad's Palworld Sleeve & Card Set, announced alongside the second booster set."],
      ["Booster box code", "English boxes carry the E-prefixed set code, for example EBP01 for Dawn of Palpagos."],
      ["Legality rule", "Sleeves must be uniform and undamaged across the whole deck at sanctioned events."],
      ["Playmat", "Optional and never legality-affecting; it protects sleeves and speeds up shuffling."],
    ],
    pitfalls: [
      [
        "Mixing sleeve batches mid-season",
        "Two production batches of the same color can differ visibly. Buy enough sleeves for the season in one order.",
      ],
      [
        "Buying a booster box without checking the prefix",
        "A box without the E prefix is not the English printing, and its cards will not match the codes used in the card database or deck builder.",
      ],
      [
        "Using clear-backed sleeves at an event",
        "Clear backs let opponents read card edges. Sanctioned play expects opaque, uniform backs.",
      ],
      [
        "Buying a playmat before a deck",
        "Accessories do not improve win rate. Spend on the deck list first and validate it in the deck builder.",
      ],
    ],
    faq: [
      [
        "What size sleeves fit Palworld TCG cards?",
        "Standard-size sleeves, the same size used for most modern trading card games. Buy a spare pack so you can replace a damaged sleeve without mixing batches.",
      ],
      [
        "Is there an official Palworld playmat?",
        "Bushiroad's Palworld accessory line includes card-game accessories announced alongside the booster sets. A playmat is never required for legality — uniform sleeves are the only accessory rule that matters.",
      ],
      [
        "How many cards are in a Palworld booster box?",
        "Box configuration is printed on the box itself and differs by product. Read the pack count from the official product listing rather than a marketplace description, which is frequently wrong.",
      ],
      [
        "Do English Palworld booster boxes use different codes?",
        "Yes. English cards and boxes carry an E prefix — EBP01 for Dawn of Palpagos — which is the code the Pal Stars card database and card checker use.",
      ],
      [
        "Can I use non-Palworld sleeves in a Palworld deck?",
        "Yes, provided every sleeve in the deck is identical, opaque-backed and undamaged. Branded sleeves are cosmetic, not a requirement.",
      ],
    ],
  },
  {
    slug: "palworld-gift-guide",
    title: "Palworld Gift Guide",
    desc: "What to buy a Palworld player by budget and by what they actually play — cards, plush, accessories or sealed product — with the checks that stop a gift arriving as an unlicensed copy.",
    keywords:
      "palworld gift ideas, palworld gifts, palworld christmas gifts, palworld birthday gift, gifts for palworld fans, palworld stocking stuffers, best palworld merch to buy",
    hub: "/cards",
    hubLabel: "Palworld card database",
    tag: "Merch",
    question: "What is a good gift for a Palworld player?",
    answer:
      "Match the gift to how they play. A card-game player wants sealed booster product or sleeves in the right set code; a base-builder or collector wants plush or a figure of their main Pal; anyone under a small budget is best served by a licensed accessory such as an acrylic stand or pin. The one universal rule is to buy licensed — an unlicensed copy is the most common way a Palworld gift disappoints.",
    steps: [
      [
        "Find out whether they play the card game",
        "It changes the whole category. Card players want sealed product and sleeves; game-only players usually do not want cards at all.",
      ],
      [
        "Ask which Pal they use most",
        "A plush or figure of the Pal they actually run lands far better than a generic one. Their favourite is usually whatever they ride or farm with.",
      ],
      [
        "Set the budget before the category",
        "Under a small budget, licensed accessories beat a cheap unlicensed plush every time. Sealed booster product and figures sit at the top of the range.",
      ],
      [
        "Verify the licence before you check out",
        "Read the licensee name on the tag, backing card or box in the listing photos. This single check removes almost every bad gift outcome in this category.",
      ],
      [
        "Order early enough for a licensed retailer's shipping",
        "Licensed stock sells through around gifting season and resale prices spike. Ordering ahead is what keeps you off the resale market.",
      ],
    ],
    data: [
      ["Card-game player", "Sealed booster product in the correct E-prefixed English set code, plus standard-size sleeves."],
      ["Collector", "Licensed plush or figure of the Pal they actually play; check the licensee tag."],
      ["Small budget", "Licensed accessory — acrylic stand, pin or packaged keychain with a printed copyright line."],
      ["Free option", "A Pal Stars account: 12 free packs a day in the pack opening game and daily Palsino spins, no purchase involved."],
      ["Universal check", "Licensee name plus Pocketpair copyright printed on the product or its packaging."],
    ],
    pitfalls: [
      [
        "Buying cards for someone who does not play the card game",
        "Cards without a playgroup sit in a drawer. Confirm they play before buying sealed product.",
      ],
      [
        "Guessing their favourite Pal",
        "A plush of the wrong Pal is the most common miss in this category. One question beats the guess.",
      ],
      [
        "Leaving it to gifting season",
        "Licensed stock thins and resale premiums appear. Order while the licensed retailer still has stock.",
      ],
      [
        "Choosing a cheap unlicensed plush over a licensed accessory",
        "At the same price, a licensed small item is a better gift than an unlicensed large one, and it can be returned.",
      ],
    ],
    faq: [
      [
        "What should I buy a Palworld fan for Christmas?",
        "If they play the card game, sealed booster product in the English E-prefixed set code. If they only play the game, a licensed plush or figure of the Pal they use most. On a small budget, a licensed packaged accessory beats an unlicensed plush.",
      ],
      [
        "Are Palworld cards a good gift?",
        "They are, for someone who already plays or collects. Buy the English printing — codes start with E, such as EBP01 — so the cards match the card database and are legal in English organised play.",
      ],
      [
        "What is the cheapest official Palworld gift?",
        "A packaged licensed accessory such as an acrylic stand, pin or keychain. Check for the printed Pocketpair copyright line on the backing card before ordering.",
      ],
      [
        "Is there a free Palworld gift option?",
        "Yes — a free Pal Stars account includes 12 booster packs a day in the pack opening game, daily Palsino spins and a binder to collect pulls, with no purchase at any point.",
      ],
      [
        "How early should I order Palworld merch for a gift?",
        "Order at least a few weeks ahead. Licensed stock sells through around gifting season, and the gap is usually filled by resale listings at a premium.",
      ],
    ],
  },
  {
    slug: "palworld-collectible-figures-and-limited-editions",
    title: "Palworld Collectible Figures",
    desc: "Palworld collectible figures, statues and limited-edition releases: which partners produce them, how pre-orders and allocations work, and how to protect the value of a sealed collectable.",
    keywords:
      "palworld figures, palworld action figure, palworld statue, palworld limited edition, palworld collectibles, palworld figure pre order, palworld collector edition",
    hub: "/pals",
    hubLabel: "Palworld Pal database",
    tag: "Merch",
    question: "Who makes official Palworld collectible figures?",
    answer:
      "Palworld figures and collectables come from licensed partners announced by Pocketpair, with Bushiroad and The Loyal Subjects the named partners for figure and blind-box product lines. Figures in this category are typically pre-order-driven and produced to allocation, which means the licensed window closes months before the item ships and everything after that is resale.",
    steps: [
      [
        "Track the announcement, not the resale listing",
        "Figures are announced with a pre-order window and a ship date months later. Getting into the pre-order window is the whole game in this category.",
      ],
      [
        "Confirm the scale and material in the listing",
        "Licensed figure listings state scale, height in centimetres and material (PVC, ABS, vinyl). A listing missing all three is not a licensed product page.",
      ],
      [
        "Pre-order from one retailer only",
        "Duplicate pre-orders across retailers to 'guarantee' one usually result in two charges at ship time. Pick a licensed retailer and cancel the rest.",
      ],
      [
        "Keep the box if you care about value",
        "For collectables, packaging condition drives resale value more than the figure itself. Store the box flat and out of direct light.",
      ],
      [
        "Check the Pal is official before you commit",
        "Look the Pal up in the Pal database. Fan-made resin figures of non-existent 'variants' are a recurring listing pattern in this category.",
      ],
    ],
    data: [
      ["Named partners", "Bushiroad and The Loyal Subjects for Palworld figure and blind-box product lines."],
      ["Buying model", "Pre-order driven with allocation; the licensed window typically closes months before the ship date."],
      ["Required listing detail", "Scale, height in centimetres and material — a licensed product page always states all three."],
      ["Value driver", "For sealed collectables, packaging condition matters as much as the figure."],
      ["Verification", "Confirm the Pal exists officially on its /pals page before ordering any figure of it."],
    ],
    pitfalls: [
      [
        "Missing the pre-order window",
        "Allocation-produced figures do not restock. Once the window closes, every listing is resale at a premium.",
      ],
      [
        "Duplicate pre-orders across retailers",
        "Both can charge on ship day. Keep a single pre-order and cancel the others as soon as one is confirmed.",
      ],
      [
        "Discarding the box",
        "Resale value on collectables collapses without the original packaging. Keep it from day one.",
      ],
      [
        "Buying a figure of a Pal that does not exist",
        "Fan-made resin figures are sold as 'limited edition variants'. Only Pals listed in the official Pal database are real.",
      ],
    ],
    faq: [
      [
        "Are there official Palworld action figures?",
        "Yes — figure and collectable lines come from licensed partners including Bushiroad and The Loyal Subjects. Check the partner's own product page for scale, height and material before ordering.",
      ],
      [
        "How do Palworld figure pre-orders work?",
        "A partner announces the figure with a pre-order window and a ship date several months out. Production is to allocation, so once the window closes the item does not restock at retail.",
      ],
      [
        "Do Palworld limited editions restock?",
        "Allocation-produced limited editions generally do not. Anything available after the window has closed is a resale listing, priced accordingly.",
      ],
      [
        "Should I keep the box for a Palworld collectable?",
        "Yes. For sealed collectables, packaging condition affects resale value as much as the figure's condition. Store the box flat, dry and out of direct sunlight.",
      ],
      [
        "How do I spot a fan-made Palworld figure?",
        "Missing scale and material details, resin construction, and a Pal name that does not appear in the official Pal database. Any of those three means it is not licensed.",
      ],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here pairs with the ${t.hubLabel} at ${t.hub}.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What is actually confirmed", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        {
          heading: `Use it with the ${t.hubLabel}`,
          text: `${hubSentence} Open ${t.hub} and work from the live data there rather than a remembered price or release date.`,
        },
      ],
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The order to work through, with the mistake each step removes.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `Confirmed details behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "The failure modes that cost buyers the most money on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions people ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(2),
    },
  ];
}

export const MERCH_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-08-26",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each merchandise cluster supports, for internal-link rendering. */
export const MERCH_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
